import styles from './styles.module.scss';

import {
  Info,
  Settings,
  ThumbDownAltOutlined,
  ThumbsUpDownOutlined,
  ThumbUpAltOutlined,
} from '@mui/icons-material';
import {
  Box,
  IconButton,
  Paper,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import clsx from 'clsx';
import { useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Divider } from '@work4all/components/lib/dataDisplay/divider/Divider';
import { CheckboxRadioItem } from '@work4all/components/lib/input/checkbox-radio-item';
import { LabeledInput } from '@work4all/components/lib/input/labeled-input';

import { usePopoverState } from '@work4all/data/lib/hooks/usePopoverState';

import { currencyAsSign } from '@work4all/utils';

import { CurrencyExchangeInfoContext } from '../../../containers/mask-overlays/mask-overlay/views/inbound-invoice/currency-exchange-info-context';
import { settings, useSetting } from '../../../settings';
import { useProjectControllingColumnSettings } from '../project-table/hooks/use-project-controlling-columns-setting';

import { useProjectcontrollingFields } from './ProjectControllingFields';

const TopHeader = (props: { category: string }) => {
  const { t } = useTranslation();
  const settingsPopoverState = usePopoverState();
  const infoPopoverState = usePopoverState();
  const settingsBtnRef = useRef(null);
  const infoBtnRef = useRef(null);
  const setting = useProjectControllingColumnSettings(props.category);
  const fields = useProjectcontrollingFields(props.category);
  const projectControllingCalcValuesComesFrom = useSetting(
    settings.projectControllingPlannedValuesComesFrom()
  );

  const projectControllingDBColors = useSetting(
    settings.projectControllingDBColors()
  );

  const projectControllingShowDecimals = useSetting(
    settings.projectControllingShowDecimals()
  );

  const projectControllingTrendDelta = useSetting(
    settings.projectControllingTrendDelta()
  );

  const currencyExchangeInfo = useContext(CurrencyExchangeInfoContext);
  const defaultCurrency = currencyExchangeInfo?.defaultCurrency;

  const currencySymbol = currencyAsSign(defaultCurrency?.name);

  const exampleValues = {
    planned: {
      externalcontribution: '200 ' + currencySymbol,
      internalcontribution: '500 ' + currencySymbol,
      totalcosts: '700 ' + currencySymbol,
      sales: '1.400 ' + currencySymbol,
      dbinEUR: '700 ' + currencySymbol,
      dbinPROCENT: '50 %',
    },
    actual: {
      externalcontribution: '200 ' + currencySymbol,
      internalcontribution: '500 ' + currencySymbol,
      costswithoutreceipt: '100 ' + currencySymbol,
      totalcosts: '800 ' + currencySymbol,
      sales: '400 ' + currencySymbol,
      dbinEUR: '-400 ' + currencySymbol,
      dbinPROCENT: '-100 %',
    },
  };

  return (
    <>
      <Popover {...infoPopoverState} anchorEl={infoBtnRef.current}>
        <Paper>
          <Typography variant="h4" p="0 1rem" pt="0.5rem">
            {t('COMMON.EXAMPLE')}
          </Typography>
          <table cellSpacing={0} className={styles.legend}>
            {fields.map((field) => {
              const hasBorder =
                (props.category === 'planned' &&
                  field.key === 'externalcontribution') ||
                (props.category === 'actual' &&
                  field.key === 'costswithoutreceipt');
              const paddingBottom = field.key === 'totalcosts';

              const value = exampleValues[props.category]?.[field.key];
              const color =
                props.category === 'actual' && field.key === 'dbinPROCENT'
                  ? 'var(--error)'
                  : props.category === 'planned' && field.key === 'dbinPROCENT'
                  ? 'var(--success)'
                  : 'var(--text03)';

              return (
                <>
                  <tr
                    className={clsx({ [styles.paddingBottom]: paddingBottom })}
                  >
                    <td>
                      <Typography variant="caption">
                        {t(
                          `STATISTIC.PROJECTCONTROLLING.LEGEND.${props.category}.TITLE.${field.key}`
                        )}
                        :{' '}
                      </Typography>
                    </td>
                    <td align="right">
                      <Typography variant="caption" color={color}>
                        {value}
                      </Typography>
                    </td>
                    <td>
                      <Typography color="var(--text01)" variant="caption">
                        {t(
                          `STATISTIC.PROJECTCONTROLLING.LEGEND.${props.category}.DESC.${field.key}`,
                          {
                            receipt: t(
                              'COMMON.' +
                                projectControllingCalcValuesComesFrom.value.toUpperCase()
                            ),
                          }
                        )}
                      </Typography>
                    </td>
                  </tr>
                  {hasBorder && (
                    <tr className={styles.border}>
                      <td colSpan={3}></td>
                    </tr>
                  )}
                </>
              );
            })}
          </table>
        </Paper>
      </Popover>
      <Popover {...settingsPopoverState} anchorEl={settingsBtnRef.current}>
        <Paper>
          <Stack p="0.5rem 1rem" pt="0">
            {!['comparison', 'targetContributionMargin'].includes(
              props.category
            ) && (
              <>
                <Divider title={t('MASK.COLUMNS')} />
                {fields.map((field) => (
                  <CheckboxRadioItem
                    key={field.key}
                    checked={setting.value[field.key]}
                    onChange={(e) => {
                      setting.set({
                        ...setting.value,
                        [field.key]: e.target.checked,
                      });
                    }}
                    label={
                      field.label +
                      (field.key !== 'dbinPROCENT' ? `/${currencySymbol}` : '')
                    }
                    horizontalPadding={false}
                  />
                ))}
              </>
            )}
            {props.category === 'planned' && (
              <>
                <Divider
                  title={t('STATISTIC.PROJECTCONTROLLING.calcvaluescomefrom')}
                />
                <CheckboxRadioItem
                  control="radio"
                  label={t(`COMMON.CALCULATION`, { count: 3 })}
                  checked={
                    projectControllingCalcValuesComesFrom.value ===
                    'calculation'
                  }
                  onChange={() =>
                    projectControllingCalcValuesComesFrom.set('calculation')
                  }
                  horizontalPadding={false}
                />
                <CheckboxRadioItem
                  control="radio"
                  label={t(`COMMON.OFFER`, { count: 3 })}
                  checked={
                    projectControllingCalcValuesComesFrom.value === 'offer'
                  }
                  onChange={() =>
                    projectControllingCalcValuesComesFrom.set('offer')
                  }
                  horizontalPadding={false}
                />
                <CheckboxRadioItem
                  control="radio"
                  label={t(`COMMON.CONTRACT`, { count: 3 })}
                  checked={
                    projectControllingCalcValuesComesFrom.value === 'contract'
                  }
                  onChange={() =>
                    projectControllingCalcValuesComesFrom.set('contract')
                  }
                  horizontalPadding={false}
                />
              </>
            )}
            {['planned', 'actual', 'targetContributionMargin'].includes(
              props.category
            ) && (
              <>
                <Divider title={t('STATISTIC.PROJECTCONTROLLING.DB_TITLE')} />

                <Stack gap="1rem">
                  <Stack direction="row" gap="1rem">
                    <LabeledInput
                      style={{ width: '6rem' }}
                      endAdornment={
                        <Typography color="var(--text03)" pt="1rem">
                          &nbsp;%
                        </Typography>
                      }
                      type="number"
                      label={t('STATISTIC.PROJECTCONTROLLING.DB_TARGET')}
                      value={projectControllingDBColors.value.target}
                      onChange={(e) =>
                        projectControllingDBColors.set({
                          ...projectControllingDBColors.value,
                          target: Number(e.target.value),
                        })
                      }
                    />
                    <LabeledInput
                      style={{ width: '6rem' }}
                      endAdornment={
                        <Typography color="var(--text03)" pt="1rem">
                          &nbsp;%
                        </Typography>
                      }
                      type="number"
                      label={t('STATISTIC.PROJECTCONTROLLING.DB_DELTA')}
                      value={projectControllingDBColors.value.delta}
                      onChange={(e) =>
                        projectControllingDBColors.set({
                          ...projectControllingDBColors.value,
                          delta: Number(e.target.value),
                        })
                      }
                    />
                  </Stack>
                  <Stack
                    justifyContent="center"
                    direction="row"
                    gap="0.5rem"
                    alignItems="center"
                    alignContent="center"
                  >
                    <Typography color="var(--error)" variant="caption">
                      &lt;&nbsp;
                      {projectControllingDBColors.value.target -
                        projectControllingDBColors.value.delta}{' '}
                      %
                    </Typography>
                    {projectControllingDBColors.value.delta > 0 && (
                      <>
                        <Typography variant="caption">|</Typography>
                        <Typography color="var(--alert)" variant="caption">
                          {projectControllingDBColors.value.target -
                            projectControllingDBColors.value.delta}
                          &nbsp;-&nbsp;
                          {projectControllingDBColors.value.target +
                            projectControllingDBColors.value.delta}
                          &nbsp;%
                        </Typography>
                      </>
                    )}
                    <Typography variant="caption">|</Typography>
                    <Typography color="var(--success)" variant="caption">
                      &gt;&nbsp;
                      {projectControllingDBColors.value.target +
                        projectControllingDBColors.value.delta}{' '}
                      %
                    </Typography>
                  </Stack>
                </Stack>
              </>
            )}
            {!['comparison', 'targetContributionMargin'].includes(
              props.category
            ) && (
              <>
                <Divider title={t('MASK.OPTIONS')} />
                <CheckboxRadioItem
                  label={t('STATISTIC.PROJECTCONTROLLING.SHOW_DECIMALS')}
                  checked={projectControllingShowDecimals.value}
                  onChange={() =>
                    projectControllingShowDecimals.set(
                      !projectControllingShowDecimals.value
                    )
                  }
                />
              </>
            )}
            {props.category === 'comparison' && (
              <Stack gap="0.5rem">
                <Divider title={t('MASK.OPTIONS')} />
                <Typography variant="caption" width="13rem">
                  {t('STATISTIC.PROJECTCONTROLLING.TREND.EXPLANATION')}
                </Typography>
                <Stack direction="row" gap="1rem">
                  <LabeledInput
                    style={{ width: '6rem', height: 'fit-content' }}
                    endAdornment={
                      <Typography color="var(--text03)" pt="1rem">
                        &nbsp;%
                      </Typography>
                    }
                    type="number"
                    label={t('STATISTIC.PROJECTCONTROLLING.DB_DELTA')}
                    value={projectControllingTrendDelta.value}
                    onChange={(e) =>
                      projectControllingTrendDelta.set(Number(e.target.value))
                    }
                  />
                  <Stack gap="1rem">
                    <Stack direction="row" gap="0.5rem" alignItems="center">
                      <ThumbDownAltOutlined color="error" fontSize="small" />
                      <Typography variant="caption" color="var(--text03)">
                        &lt; {0 - projectControllingTrendDelta.value} %
                      </Typography>
                    </Stack>
                    <Stack direction="row" gap="0.5rem" alignItems="center">
                      <ThumbsUpDownOutlined color="warning" />
                      <Typography variant="caption" color="var(--text03)">
                        {0 - projectControllingTrendDelta.value} -{' '}
                        {0 + projectControllingTrendDelta.value} %
                      </Typography>
                    </Stack>
                    <Stack direction="row" gap="0.5rem" alignItems="center">
                      <ThumbUpAltOutlined color="success" />
                      <Typography variant="caption" color="var(--text03)">
                        &gt; {0 + projectControllingTrendDelta.value} %
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            )}
          </Stack>
        </Paper>
      </Popover>
      <Stack direction="row" className={styles.resourceColHeader}>
        <Typography
          variant="caption"
          padding="0.25rem"
          maxWidth="100%"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
          flex={1}
          p={
            ['comparison', 'plannedTimes', 'targetContributionMargin'].includes(
              props.category
            )
              ? '1rem'
              : undefined
          }
        >
          {props.category === 'planned'
            ? t('STATISTIC.PROJECTCONTROLLING.planned_colHeader', {
                from: t(
                  'COMMON.' +
                    projectControllingCalcValuesComesFrom.value.toUpperCase()
                ),
              })
            : t('STATISTIC.PROJECTCONTROLLING.' + props.category)}

          {props.category === 'targetContributionMargin' &&
            ' ' + projectControllingDBColors.value.target + '%'}
        </Typography>
        <Box className={styles.cta}>
          {!['comparison', 'plannedTimes', 'targetContributionMargin'].includes(
            props.category
          ) && (
            <IconButton
              size="small"
              ref={infoBtnRef}
              onClick={() => infoPopoverState.handleClick()}
            >
              <Info color="primary" fontSize="small" />
            </IconButton>
          )}
          {!['plannedTimes'].includes(props.category) && (
            <IconButton
              size="small"
              ref={settingsBtnRef}
              onClick={() => settingsPopoverState.handleClick()}
            >
              <Settings fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Stack>
    </>
  );
};

const SubHeader = (props: { category: string }) => {
  const setting = useProjectControllingColumnSettings(props.category);
  const fields = useProjectcontrollingFields(props.category);
  const activeFields = fields.filter((field) => setting.value[field.key]);

  const currencyExchangeInfo = useContext(CurrencyExchangeInfoContext);
  const defaultCurrency = currencyExchangeInfo?.defaultCurrency;

  const currencySymbol = currencyAsSign(defaultCurrency?.name);

  return (
    <Stack
      direction="row"
      width="100%"
      gap="0.5rem"
      borderTop="1px solid var(--ui04)"
    >
      {activeFields.map((field) => (
        <Typography
          key={field.label}
          variant="caption"
          flex="1"
          paddingTop="0.25rem"
        >
          {field.label}
          {field.key !== 'dbinPROCENT' ? `/${currencySymbol}` : ''}
        </Typography>
      ))}
    </Stack>
  );
};

export const ProjectControllingColumnHeader = (props: { category: string }) => {
  return (
    <Stack
      style={{
        textAlign: 'center',
        width: '100%',
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        overflow: 'hidden',
      }}
    >
      <TopHeader category={props.category} />
      {!['comparison', 'plannedTimes', 'targetContributionMargin'].includes(
        props.category
      ) && <SubHeader category={props.category} />}
    </Stack>
  );
};
